import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { isDevFlag, useConditionalFeature } from '@americanexpress/conditional-features';
import { useSelector } from 'react-redux';
import { getActiveLocaleSelector, getIsLoggedInSelector, getTenancySelector } from '@americanexpress/travel-selectors';
import { withRouter } from '@americanexpress/one-app-router';
import { Experiment, useFeatureFlag, Variant } from '@americanexpress/one-experiments';
import { Surface } from '@americanexpress/dls-react';
import { RenderModule } from 'holocron';
import {
  hasFooterLinksRule, hasLegalRule, hasMultipleRewardsRule, hasProjectAdsRule,
  hasTravelCreditOverlayRule, hasVacationPackagesPathRule, hasVacationRentalsRule,
} from '../constants/featureRulesConstants';
import Hero from './Hero/Hero';
import PageMeta from './PageMeta/PageMeta';
import getLineOfBusinessFromPath from '../utils/getLineOfBusinessUtils';
import { generateNextPathFromLineOfBusiness } from '../utils/generateNextPathFromLineOfBusiness';
import Terms from './Terms/Terms';
import CardOffers from './CardOffersContainer/CardOffersContainer';
import { HOTELS } from '../constants/lineOfBusinessConstants';
import StrategicMessage from './StrategicMessage/StrategicMessage';
import { useMembershipApi } from '../hooks/membershipDomainDataHooks';
import { isVREligible } from '../utils/membership';
import getIsWaitingForMultipleRewardsSelector from '../selectors/getIsWaitingForMultipleRewardsSelector';
import {
  DISABLE_PACKAGES_FF,
  VACATION_RENTALS_FF,
  FOR_YOU_EXPERIMENT,
  HOMEPAGE_REDESIGN_EXPERIMENT,
  MARKETING_TILES_EXPERIMENT,
} from '../constants/oneXpConstants';
import { InspirationArticles } from './InspirationalArticles/InspirationArticles';
import VariantHero from './Hero/VariantHero';
import {
  FOOTER_MODULE_NAME, LEGAL_MODULE_NAME, CREDIT_MODULE_NAME, ADS_MODULE_NAME, MR_MODULE_NAME,
} from '../constants/modules';
import ForYou from './ForYou/ForYou';

export const bindOnSelectedTabChange = ({
  selectedTab, setSelectedTab,
}) => (nextSelectedTab) => {
  if (selectedTab === null && nextSelectedTab === HOTELS) {
    return;
  }
  if (nextSelectedTab !== selectedTab) {
    setSelectedTab(nextSelectedTab);
  }
};

export const getInitialLineOfBusiness = (pathname, isPackagesDisabledByFF) => {
  if (pathname.endsWith('/travel') || pathname.endsWith('/travel/')) {
    return null;
  }
  return getLineOfBusinessFromPath(isPackagesDisabledByFF, pathname);
};

const HomePage = ({
  location: { pathname },
  router,
}) => {
  const tenancy = useSelector(getTenancySelector);
  const locale = useSelector(getActiveLocaleSelector);
  const isLoggedIn = useSelector(getIsLoggedInSelector);
  const isWaitingForMultipleRewards = useSelector(getIsWaitingForMultipleRewardsSelector);
  const hasVacationPackagesPath = useConditionalFeature(hasVacationPackagesPathRule);

  const { properties } = useFeatureFlag({ featureFlagName: DISABLE_PACKAGES_FF });
  const isPackagesDisabledByFF = properties?.[tenancy]?.includes(locale);
  const prevSelectedTab = useRef(getInitialLineOfBusiness(pathname, isPackagesDisabledByFF));
  const [selectedTab, setSelectedTab] = useState(prevSelectedTab.current);

  const { data: membershipData } = useMembershipApi();

  const isEligibleForVacationRentals = isVREligible(membershipData);
  const hasVacationRentals = useConditionalFeature(hasVacationRentalsRule);
  const { enabled: vrEnabled } = useFeatureFlag({ featureFlagName: VACATION_RENTALS_FF });

  const showVacationRentals = hasVacationRentals // Checks for eligible Locale
    && vrEnabled // Checks that the VR FF is enabled
    && (isEligibleForVacationRentals // Checks that the star eligible flag is set
      || !isLoggedIn); // Guest users should see the vr tab

  useEffect(() => {
    if (pathname.includes('demo')) {
      return;
    }
    if (selectedTab !== prevSelectedTab.current) {
      prevSelectedTab.current = selectedTab;
      router.push(
        generateNextPathFromLineOfBusiness(
          selectedTab, router.location, hasVacationPackagesPath
        )
      );
    }
  }, [selectedTab, locale, pathname, hasVacationPackagesPath, router]);

  const hasAds = useConditionalFeature(hasProjectAdsRule);
  const hasTravelCreditOverlay = useConditionalFeature(hasTravelCreditOverlayRule);
  const hasFooterLinks = useConditionalFeature(hasFooterLinksRule);
  const hasLegal = useConditionalFeature(hasLegalRule);
  const hasMultipleRewards = useConditionalFeature(hasMultipleRewardsRule);

  const variantHero = (
    <VariantHero
      isLoggedIn={isLoggedIn}
      selectedTab={selectedTab}
      setSelectedTab={bindOnSelectedTabChange({ selectedTab, setSelectedTab })}
      pathname={pathname}
    />
  );

  return (
    <>
      <PageMeta selectedTab={selectedTab} />
      {isDevFlag('strategicInitiative') && <StrategicMessage />}
      <Experiment
        experimentName={HOMEPAGE_REDESIGN_EXPERIMENT}
        dimensionName="search_widget"
        defaultVariant="control"
      >
        <Variant variant="control">
          <Hero
            isLoggedIn={isLoggedIn}
            setSelectedTab={bindOnSelectedTabChange({ selectedTab, setSelectedTab })}
            pathname={pathname}
          />
        </Variant>
        <Variant variant="widget_a_with_hero">{variantHero}</Variant>
        <Variant variant="widget_a_no_hero">{variantHero}</Variant>
        <Variant variant="widget_b_with_hero">{variantHero}</Variant>
        <Variant variant="widget_b_no_hero">{variantHero}</Variant>
      </Experiment>
      <Surface variant="foreground">
        <Experiment experimentName={FOR_YOU_EXPERIMENT} dimensionName="homepage" defaultVariant="control">
          <Variant variant="mosaic">
            <ForYou />
          </Variant>
        </Experiment>
        <Experiment
          experimentName={MARKETING_TILES_EXPERIMENT}
          dimensionName="homepage"
          defaultVariant="control"
        >
          <Variant variant="control">
            <CardOffers />
          </Variant>
          <Variant variant="mosaic">
            <p className="text-align-center">The Mosiac Marketing Tiles</p>
          </Variant>
        </Experiment>
        <InspirationArticles />
      </Surface>
      {hasAds && (
        <>
          <RenderModule
            moduleName={ADS_MODULE_NAME}
            props={{
              page: 'travel',
              placementId: 'TravelWeb_Homepage_Top',
              backgroundColor: 'white',
              placementType: 'FLUID',
            }}
          />
          <RenderModule
            moduleName={ADS_MODULE_NAME}
            props={{
              page: 'travel',
              placementId: 'TravelWeb_Homepage_Bottom',
              backgroundColor: 'white',
              placementType: 'SMALL_BANNER',
            }}
          />
        </>
      )}
      {hasFooterLinks && <RenderModule moduleName={FOOTER_MODULE_NAME} />}
      <Terms showVacationRentals={showVacationRentals} />
      {hasLegal && <RenderModule moduleName={LEGAL_MODULE_NAME} />}
      {hasMultipleRewards && <RenderModule moduleName={MR_MODULE_NAME} />}
      { hasTravelCreditOverlay && (
        <RenderModule
          moduleName={CREDIT_MODULE_NAME}
          props={{ isWaitingForMultipleRewards, isOverlay: true }}
        />
      )}
    </>
  );
};

HomePage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    query: PropTypes.shape({
      ERROR_MESSAGE: PropTypes.string,
    }),
  }).isRequired,
  router: PropTypes.shape({
    location: PropTypes.shape({}),
    push: PropTypes.func.isRequired,
  }).isRequired,
};

export { HomePage as TestableHomePage };

export default withRouter(HomePage);
